import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  CheckIcon,
  PaperAirplaneIcon,
  LinkIcon,
  EyeIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useDiagnosticsStore } from '../stores/diagnosticsStore';
import { FormBuilder } from '../components/diagnostics/FormBuilder';
import { PortalSettingsModal } from '../components/diagnostics/PortalSettingsModal';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import clsx from 'clsx';
import type { FormStructure } from '@affogato/shared';

export function DiagnosticDetailPage() {
  const { t } = useTranslation('diagnostics');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    selectedForm,
    isLoading,
    fetchForm,
    updateForm,
    publishForm,
    setSelectedForm,
  } = useDiagnosticsStore();

  const [localStructure, setLocalStructure] = useState<FormStructure | null>(null);
  const [localTitle, setLocalTitle] = useState('');
  const [localDescription, setLocalDescription] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [isPortalModalOpen, setIsPortalModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchForm(id);
    }
    return () => {
      setSelectedForm(null);
    };
  }, [id, fetchForm, setSelectedForm]);

  useEffect(() => {
    if (selectedForm) {
      setLocalStructure(selectedForm.structure);
      setLocalTitle(selectedForm.title);
      setLocalDescription(selectedForm.description || '');
      setHasChanges(false);
    }
  }, [selectedForm]);

  const handleStructureChange = useCallback((newStructure: FormStructure) => {
    setLocalStructure(newStructure);
    setHasChanges(true);
    setSaveStatus('idle');
  }, []);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalTitle(e.target.value);
    setHasChanges(true);
    setSaveStatus('idle');
  }, []);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalDescription(e.target.value);
    setHasChanges(true);
    setSaveStatus('idle');
  }, []);

  const handleSave = async () => {
    if (!id || !localStructure || !localTitle) return;

    setIsSaving(true);
    setSaveStatus('saving');
    try {
      await updateForm(id, {
        title: localTitle,
        description: localDescription || undefined,
        structure: localStructure,
      });
      setHasChanges(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving form:', error);
      setSaveStatus('idle');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndPublish = async () => {
    if (!id || !localStructure || !localTitle) return;

    setIsSaving(true);
    try {
      await updateForm(id, {
        title: localTitle,
        description: localDescription || undefined,
        structure: localStructure,
      });
      await publishForm(id);
      setHasChanges(false);
      await fetchForm(id);
    } catch (error) {
      console.error('Error saving and publishing form:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !selectedForm || !localStructure) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  const isPublished = selectedForm.status === 'published';
  const isArchived = selectedForm.status === 'archived';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <button
            onClick={() => navigate('/diagnostics')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={localTitle}
              onChange={handleTitleChange}
              disabled={isArchived}
              className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white bg-transparent border-0 p-0 focus:ring-0 w-full"
              placeholder={t('create.formTitle')}
            />
            <textarea
              value={localDescription}
              onChange={handleDescriptionChange}
              disabled={isArchived}
              rows={1}
              className="text-gray-500 dark:text-slate-400 bg-transparent border-0 p-0 focus:ring-0 w-full resize-none mt-1 text-sm sm:text-base"
              placeholder={t('create.formDescription')}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
          {/* Status indicator */}
          <span
            className={clsx(
              'px-3 py-1 rounded-full text-sm font-medium',
              selectedForm.status === 'published' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
              selectedForm.status === 'draft' && 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
              selectedForm.status === 'archived' && 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400'
            )}
          >
            {t(`status.${selectedForm.status}`)}
          </span>

          {/* Save status */}
          {saveStatus === 'saving' && (
            <span className="text-sm text-gray-500 dark:text-slate-400 flex items-center gap-1">
              <Spinner size="sm" />
              {t('builder.saving')}
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
              <CheckIcon className="w-4 h-4" />
              {t('builder.saved')}
            </span>
          )}

          {/* Actions */}
          {isPublished && (
            <div className="flex items-center gap-4 mr-2">
              <button
                onClick={() => setIsPortalModalOpen(true)}
                className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                title={t('card.sharePortal')}
              >
                <LinkIcon className="w-4 h-4" />
              </button>
              {selectedForm.responseCount !== undefined && selectedForm.responseCount > 0 && (
                <>
                  <button
                    onClick={() => navigate(`/diagnostics/${id}/responses`)}
                    className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <EyeIcon className="w-4 h-4" />
                    <span>{selectedForm.responseCount}</span>
                  </button>
                  <button
                    onClick={() => navigate(`/diagnostics/${id}/analysis`)}
                    className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <ChartBarIcon className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          )}

          {!isArchived && (
            <>
              <Button
                variant="secondary"
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
              >
                {t('builder.save')}
              </Button>

              {selectedForm.status === 'draft' && (
                <Button onClick={handleSaveAndPublish} disabled={isSaving}>
                  <PaperAirplaneIcon className="w-4 h-4" />
                  {t('builder.saveAndPublish')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Form Builder */}
      <FormBuilder
        structure={localStructure}
        onChange={handleStructureChange}
        readOnly={isArchived}
      />

      {/* Portal Settings Modal */}
      {selectedForm && (
        <PortalSettingsModal
          isOpen={isPortalModalOpen}
          onClose={() => setIsPortalModalOpen(false)}
          formId={selectedForm.id}
          portal={selectedForm.portal}
        />
      )}
    </div>
  );
}
